import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";

// PrimeNG Imports
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { DialogModule } from "primeng/dialog";
import { FloatLabelModule } from "primeng/floatlabel";
import { InputTextModule } from "primeng/inputtext";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { MessageModule } from "primeng/message";
import { MultiSelectModule } from "primeng/multiselect";
import { Popover, PopoverModule } from "primeng/popover";
import { SelectModule } from "primeng/select";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { MessageService } from "primeng/api";
import { ToastModule } from "primeng/toast";
import { SkeletonModule } from "primeng/skeleton";
import { TabsModule } from "primeng/tabs";

// Services
import {
  QPilotAccessToken,
  QPilotService,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";

// Interfaces
import {
  DunningSettings,
  DunningSettingsForm,
  ErrorCodeOption,
  RetryIntervalOption,
} from "../../interfaces/dunning-settings.interface";

// Constants
import {
  ALLOWED_RETRY_ATTEMPTS,
  DEFAULT_NOTIFICATION_TEMPLATES,
  DUNNING_ACTION_OPTIONS,
  DUNNING_DOCS,
  DYNAMIC_FIELDS,
  ERROR_CODE_OPTIONS,
  getRetryIntervalOptions,
} from "../../constants/dunning.constants";

@Component({
  selector: "app-site-dunning-settings",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    DialogModule,
    FloatLabelModule,
    InputTextModule,
    ToggleSwitchModule,
    MessageModule,
    MultiSelectModule,
    PopoverModule,
    SelectModule,
    ProgressSpinnerModule,
    ToastModule,
    SkeletonModule,
    TabsModule,
  ],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
  templateUrl: "./site-dunning-settings.component.html",
  styleUrls: ["./site-dunning-settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteDunningSettingsComponent implements OnInit, OnDestroy {
  @ViewChild("dynamicFieldsPopover") dynamicFieldsPopover!: Popover;
  @ViewChild("learnMorePopover") learnMorePopover!: Popover;

  // Web Component / Embedded Mode Inputs
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() embeddedMode: boolean = false;

  private readonly destroy$ = new Subject<void>();

  // State signals
  loading = signal(true);
  saving = signal(false);
  showUpgradeDialog = signal(false);

  // Tab navigation
  activeTabIndex = 0;

  // Form
  formGroup!: FormGroup<DunningSettingsForm>;

  // Original settings for dirty detection
  private originalSettings: DunningSettings | null = null;

  // Site ID
  siteId!: number;

  // Subscription tier
  allowedForSubscription = true;
  currentTierName = "";
  upgradeUrl = "";

  // Options for dropdowns
  readonly errorCodeOptions: ErrorCodeOption[] = ERROR_CODE_OPTIONS;
  readonly retryIntervalOptions: RetryIntervalOption[] =
    getRetryIntervalOptions();
  readonly retryAttemptOptions = ALLOWED_RETRY_ATTEMPTS.map((n) => ({
    value: n,
    label: `${n} ${n === 1 ? "time" : "times"}`,
  }));
  readonly dunningActionOptions = DUNNING_ACTION_OPTIONS;
  readonly dynamicFields = DYNAMIC_FIELDS;
  readonly dunningDocs = DUNNING_DOCS;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly qpilotService: QPilotService,
    private readonly sessionService: SessionService,
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Check for embedded mode from HTML attributes (web component mode)
    const element = this.elementRef.nativeElement as HTMLElement;
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");

    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get siteId from attributes (kebab-case) OR inputs (camelCase)
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId");

    // Set values from attributes if they exist (for web component mode)
    if (siteIdString) {
      this.siteId = parseInt(siteIdString, 10);
    }

    // Get token from attribute and convert
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    // Create inputToken from attribute if:
    // 1. We have a token string from attribute
    // 2. AND inputToken is either missing, not an object, or doesn't have valid TokenBearerAuth
    // Note: Angular Elements may bind the attribute as a raw string, so we check for proper structure
    if (
      tokenString &&
      (!this.inputToken ||
        typeof this.inputToken !== "object" ||
        !this.inputToken.TokenBearerAuth)
    ) {
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }

    // Initialize based on mode
    if (this.embeddedMode || this.inputSiteId) {
      // Embedded mode - use inputs/attributes
      if (this.siteId) {
        this.loadDunningSettings();
        this.loadSubscriptionTier();
      }
    } else {
      // Standard app mode - use route params
      this.route.parent?.paramMap
        .pipe(takeUntil(this.destroy$))
        .subscribe((params) => {
          const siteIdParam = params.get("siteId");
          if (siteIdParam) {
            this.siteId = +siteIdParam;
            this.loadDunningSettings();
            this.loadSubscriptionTier();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Get access token from appropriate source based on mode
   * Uses inputToken if provided (embedded/web component mode),
   * otherwise falls back to session token (standard app mode)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    return this.inputToken ?? this.sessionService.accessToken ?? undefined;
  }

  /**
   * Custom validator to prevent whitespace-only values
   */
  private noWhitespaceOnly(control: AbstractControl): ValidationErrors | null {
    if (typeof control.value === "string" && control.value.trim() === "") {
      return { whitespaceOnly: true };
    }
    return null;
  }

  /**
   * Initialize the form with strongly-typed controls
   */
  private initForm(): void {
    this.formGroup = this.fb.group({
      active: this.fb.nonNullable.control(false),
      applicableErrorCodes: this.fb.nonNullable.control<number[]>(
        [],
        Validators.required,
      ),
      retryForInAttempts: this.fb.control<number | null>(
        null,
        Validators.required,
      ),
      attemptRetryEveryHours: this.fb.control<number | null>(
        null,
        Validators.required,
      ),
      dunningAction: this.fb.control<number | null>(null, Validators.required),
      firstRetry: this.fb.nonNullable.control(
        DEFAULT_NOTIFICATION_TEMPLATES.FirstRetry,
        [Validators.required, this.noWhitespaceOnly.bind(this)],
      ),
      retryAttempts: this.fb.nonNullable.control(
        DEFAULT_NOTIFICATION_TEMPLATES.RetryAttempts,
        [Validators.required, this.noWhitespaceOnly.bind(this)],
      ),
      lastRetry: this.fb.nonNullable.control(
        DEFAULT_NOTIFICATION_TEMPLATES.LastRetry,
        [Validators.required, this.noWhitespaceOnly.bind(this)],
      ),
      finished: this.fb.nonNullable.control(
        DEFAULT_NOTIFICATION_TEMPLATES.Finished,
        [Validators.required, this.noWhitespaceOnly.bind(this)],
      ),
    }) as FormGroup<DunningSettingsForm>;
  }

  /**
   * Load dunning settings from API
   */
  private loadDunningSettings(): void {
    const token = this.getAccessToken();
    if (!token) {
      console.error("No access token available");
      this.loading.set(false);
      return;
    }

    this.loading.set(true);

    this.qpilotService
      .getDunningSettings(this.siteId, token)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading.set(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (settings: DunningSettings) => {
          this.originalSettings = settings;
          this.patchFormWithSettings(settings);
        },
        error: () => {
          // Initialize with defaults if no settings exist
          this.originalSettings = this.getDefaultSettings();
          this.patchFormWithSettings(this.originalSettings);
        },
      });
  }

  /**
   * Load subscription tier info for upgrade dialog
   */
  private loadSubscriptionTier(): void {
    // TODO: Implement subscription tier check when API is available
    // For now, assume all subscriptions allow dunning
    this.allowedForSubscription = true;
  }

  /**
   * Patch form with settings from API
   */
  private patchFormWithSettings(settings: DunningSettings): void {
    this.formGroup.patchValue({
      active: settings.Active,
      applicableErrorCodes: settings.ApplicableErrorCodes || [],
      retryForInAttempts: settings.RetryForInAttempts,
      attemptRetryEveryHours: settings.AttemptRetryEveryHours,
      dunningAction: settings.DunningAction,
      firstRetry:
        settings.NotificationsTemplates?.FirstRetry ||
        DEFAULT_NOTIFICATION_TEMPLATES.FirstRetry,
      retryAttempts:
        settings.NotificationsTemplates?.RetryAttempts ||
        DEFAULT_NOTIFICATION_TEMPLATES.RetryAttempts,
      lastRetry:
        settings.NotificationsTemplates?.LastRetry ||
        DEFAULT_NOTIFICATION_TEMPLATES.LastRetry,
      finished:
        settings.NotificationsTemplates?.Finished ||
        DEFAULT_NOTIFICATION_TEMPLATES.Finished,
    });
    this.cdr.markForCheck();
  }

  /**
   * Get default settings
   */
  private getDefaultSettings(): DunningSettings {
    return {
      Active: false,
      ApplicableErrorCodes: [],
      RetryForInAttempts: 3,
      AttemptRetryEveryHours: 6,
      DunningAction: 0,
      NotificationsTemplates: { ...DEFAULT_NOTIFICATION_TEMPLATES },
    };
  }

  /**
   * Check if form has been modified
   */
  get isFormDirty(): boolean {
    if (!this.originalSettings) return false;

    const current = this.formGroup.value;
    return (
      this.originalSettings.Active !== current.active ||
      this.originalSettings.RetryForInAttempts !== current.retryForInAttempts ||
      this.originalSettings.AttemptRetryEveryHours !==
        current.attemptRetryEveryHours ||
      this.originalSettings.DunningAction !== current.dunningAction ||
      !this.arraysEqual(
        this.originalSettings.ApplicableErrorCodes,
        current.applicableErrorCodes || [],
      ) ||
      this.originalSettings.NotificationsTemplates?.FirstRetry !==
        current.firstRetry ||
      this.originalSettings.NotificationsTemplates?.RetryAttempts !==
        current.retryAttempts ||
      this.originalSettings.NotificationsTemplates?.LastRetry !==
        current.lastRetry ||
      this.originalSettings.NotificationsTemplates?.Finished !==
        current.finished
    );
  }

  /**
   * Compare two arrays for equality
   */
  private arraysEqual(a: number[], b: number[]): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
  }

  /**
   * Handle enable toggle change
   */
  onEnableToggle(): void {
    const isEnabled = this.formGroup.get("active")?.value;

    if (isEnabled && !this.allowedForSubscription) {
      this.formGroup.get("active")?.setValue(false);
      this.showUpgradeDialog.set(true);
    }
  }

  /**
   * Check if form can be saved
   * - If disabling dunning (was ON, now OFF): allow save
   * - If dunning is OFF and was already OFF: don't allow save (no meaningful change)
   * - If enabling/updating dunning (active = true): form must be valid and dirty
   */
  get canSave(): boolean {
    const isActive = this.formGroup.controls.active.value;
    const wasOriginallyActive = this.originalSettings?.Active ?? false;

    // If disabling dunning (was ON, now OFF): allow save to disable it
    if (!isActive && wasOriginallyActive) {
      return true;
    }

    // If dunning is OFF and was already OFF: don't allow save
    // (config changes are meaningless when feature is disabled)
    if (!isActive && !wasOriginallyActive) {
      return false;
    }

    // If enabling/updating dunning (active = true): form must be valid and dirty
    return this.formGroup.valid && this.isFormDirty;
  }

  /**
   * Save dunning settings
   */
  saveSettings(): void {
    const isActive = this.formGroup.controls.active.value;

    // If enabling dunning, validate the form
    if (isActive && this.formGroup.invalid) {
      this.messageService.add({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fill all required fields.",
      });
      return;
    }

    const token = this.getAccessToken();
    if (!token) {
      console.error("No access token available");
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "No access token available.",
      });
      return;
    }

    const formValue = this.formGroup.value;
    const settings: DunningSettings = {
      Active: formValue.active ?? false,
      ApplicableErrorCodes: formValue.applicableErrorCodes ?? [],
      RetryForInAttempts: formValue.retryForInAttempts ?? 3,
      AttemptRetryEveryHours: formValue.attemptRetryEveryHours ?? 6,
      DunningAction: formValue.dunningAction ?? 0,
      NotificationsTemplates: {
        Inactive: null,
        FirstRetry: (
          formValue.firstRetry ?? DEFAULT_NOTIFICATION_TEMPLATES.FirstRetry
        ).trim(),
        RetryAttempts: (
          formValue.retryAttempts ??
          DEFAULT_NOTIFICATION_TEMPLATES.RetryAttempts
        ).trim(),
        LastRetry: (
          formValue.lastRetry ?? DEFAULT_NOTIFICATION_TEMPLATES.LastRetry
        ).trim(),
        Finished: (
          formValue.finished ?? DEFAULT_NOTIFICATION_TEMPLATES.Finished
        ).trim(),
      },
    };

    this.saving.set(true);

    this.qpilotService
      .updateDunningSettings(this.siteId, settings, token)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.saving.set(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (updatedSettings: DunningSettings) => {
          this.originalSettings = updatedSettings;
          this.patchFormWithSettings(updatedSettings);
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Your dunning settings have been updated.",
          });
        },
        error: (error: unknown) => {
          console.error("Error saving dunning settings:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to save dunning settings. Please try again.",
          });
        },
      });
  }

  /**
   * Open external documentation link
   */
  openDocs(url: string): void {
    window.open(url, "_blank", "noopener,noreferrer");
  }

  /**
   * Toggle dynamic fields popover
   */
  toggleDynamicFieldsPopover(event: Event): void {
    this.dynamicFieldsPopover.toggle(event);
  }

  /**
   * Toggle learn more popover
   */
  toggleLearnMorePopover(event: Event): void {
    this.learnMorePopover.toggle(event);
  }

  /**
   * Close upgrade dialog
   */
  closeUpgradeDialog(): void {
    this.showUpgradeDialog.set(false);
  }

  /**
   * Navigate to upgrade page
   */
  goToUpgrade(): void {
    if (this.upgradeUrl) {
      window.open(this.upgradeUrl, "_blank", "noopener,noreferrer");
    }
    this.closeUpgradeDialog();
  }
}
